/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { useAuth0, User } from "@auth0/auth0-react";
import {
    AppLoadingIndicatorV1,
    getFulfilledResponseInterceptorV1,
    getRejectedResponseInterceptorV1,
    getRequestInterceptorV1,
} from "@startree-ui/platform-ui";
import axios from "axios";
import React, {
    createContext,
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from "react";
import { AuthContextProps } from "./auth-provider-loader.interfaces";

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const useAuthProvider = (): AuthContextProps => {
    return useContext(AuthContext);
};

export const AuthProviderLoader: FunctionComponent = ({ children }) => {
    const {
        isLoading,
        isAuthenticated: auth0IsAuthenticated,
        getAccessTokenSilently,
        user: auth0User,
        logout,
    } = useAuth0();
    const [isAxiosSetupWithBearerToken, setIsAxiosSetupWithBearerToken] =
        useState(false);
    const [axiosRequestInterceptorId, setAxiosRequestInterceptorId] =
        useState(0);
    const [axiosResponseInterceptorId, setAxiosResponseInterceptorId] =
        useState(0);

    /**
     * Proxy these because user's can sign up and become immediately authorized without
     * having to go through auth0 redirection
     */
    const [isAuthenticated, setIsAuthenticated] =
        useState(auth0IsAuthenticated);
    const [user, setUser] = useState<User | undefined>(auth0User);

    const handleUnauthenticatedAccess = (): void => {
        logout();
    };

    const setupAxiosWithBearerToken = (accessToken: string): void => {
        // Clear existing interceptors
        axios.interceptors.request.eject(axiosRequestInterceptorId);
        axios.interceptors.response.eject(axiosResponseInterceptorId);

        // Set new interceptors
        setAxiosRequestInterceptorId(
            axios.interceptors.request.use(getRequestInterceptorV1(accessToken))
        );
        setAxiosResponseInterceptorId(
            axios.interceptors.response.use(
                getFulfilledResponseInterceptorV1(),
                getRejectedResponseInterceptorV1(handleUnauthenticatedAccess)
            )
        );

        setIsAxiosSetupWithBearerToken(true);
    };

    // If is user is authenticated via auth0, get the bearer token
    useEffect(() => {
        if (!auth0IsAuthenticated) {
            return;
        }

        getAccessTokenSilently().then((accessToken) => {
            setupAxiosWithBearerToken(accessToken);
        });
    }, [auth0IsAuthenticated]);

    /**
     * Keeps proxies in sync with auth0
     */
    useEffect(() => {
        if (auth0User) {
            setUser(auth0User);
        }
    }, [auth0User]);
    useEffect(() => {
        if (auth0IsAuthenticated) {
            setIsAuthenticated(auth0IsAuthenticated);
        }
    }, [auth0IsAuthenticated]);

    /**
     * It is very important to not load the rest of the app so that axios is setup
     * with the Bearer token so the User provider can fetch the correct user information
     */
    if (isLoading || (isAuthenticated && !isAxiosSetupWithBearerToken)) {
        return <AppLoadingIndicatorV1 />;
    }

    return (
        <AuthContext.Provider
            value={{
                setupAxiosWithBearerToken,
                setIsAuthenticated,
                isAuthenticated,
                user,
                setUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
